import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

const caretSx = {
  display: 'inline-block',
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  mr: 0.4,
  flexShrink: 0,
};

export default function StatCard({
  title,
  value,
  color,
  gradient,
  loading,
  icon,
  helperText,
  subStat,
  subStatColor,
  onClick,
  active,
  change,
  invertChange = false,
  previousValue,
  // Caller-supplied text overriding the default `12%` label (e.g. "Up 12%") and
  // a visible caption under the indicator (e.g. "was 41") instead of the
  // default hover-only tooltip. Both optional; omitted on every other caller,
  // so the compact caret+percent+tooltip default is unchanged elsewhere.
  changeLabel,
  previousLabel,
  tooltip,
  progress,
}) {
  const theme = useTheme();
  let changeEl = null;
  if (change != null) {
    const isPositive = change > 0;
    const isNegative = change < 0;
    const isNeutral = !isPositive && !isNegative;
    const isGood = invertChange ? isPositive : isNegative;
    const changeColor = isNeutral ? 'var(--text-secondary)' : isGood ? 'var(--status-ok)' : 'var(--status-bad)';

    const caret = isPositive ? (
      <Box component="span" sx={{ ...caretSx, borderBottom: `6px solid ${changeColor}` }} />
    ) : isNegative ? (
      <Box component="span" sx={{ ...caretSx, borderTop: `6px solid ${changeColor}` }} />
    ) : null;

    const label =
      changeLabel ?? (change === Infinity ? 'new' : isNeutral ? 'no change' : `${Math.abs(Math.round(change))}%`);

    const indicator = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: '0.7rem',
          fontWeight: 600,
          color: changeColor,
          lineHeight: 1,
        }}
      >
        {caret}
        {label}
      </Box>
    );

    changeEl = previousLabel ? (
      <Stack alignItems="flex-end" spacing={0}>
        {indicator}
        <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.2, color: 'text.secondary' }}>
          {previousLabel}
        </Typography>
      </Stack>
    ) : previousValue != null ? (
      <Tooltip title={`Previous period: ${previousValue}`} arrow placement="top">
        {indicator}
      </Tooltip>
    ) : (
      indicator
    );
  }

  // Resolve the accent color for the dot and active border
  // color prop can be MUI shorthand like "warning.main" — extract a raw hex for the dot
  const accentColorMap = {
    'warning.main': theme.palette.warning.main,
    'error.main': theme.palette.error.main,
    'success.main': theme.palette.success.main,
    'info.main': theme.palette.info.main,
  };
  const accentColor = accentColorMap[color] || color || theme.palette.primary.main;

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      // A clickable card is a toggle over the filter it stands for, and the
      // only thing that said so was its border colour. A card that reports a
      // number is not a toggle and claims no state.
      role={onClick ? 'button' : undefined}
      aria-pressed={onClick ? Boolean(active) : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        borderColor: active ? accentColor : 'var(--card-border)',
        bgcolor: onClick ? 'var(--card-bg-clickable)' : 'var(--card-bg)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all var(--duration-normal) var(--easing-standard)',
        boxShadow: active
          ? `0 0 0 1px ${accentColor}, var(--card-shadow-raised-active)`
          : onClick
            ? 'var(--card-shadow-raised)'
            : 'var(--card-shadow)',
        '&:hover': onClick
          ? {
              boxShadow: 'var(--card-shadow-raised-hover)',
              borderColor: accentColor,
            }
          : {},
        '&:active': onClick
          ? {
              boxShadow: 'var(--card-shadow-raised-active)',
            }
          : {},
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          '&:last-child': { pb: 1.5 },
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          minHeight: 64,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Content */}
        <Box sx={{ position: 'relative', minWidth: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Title row with colored dot */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: icon === 'square' ? '1px' : '50%',
                bgcolor: accentColor,
                flexShrink: 0,
              }}
            />
            {tooltip ? (
              <Tooltip title={tooltip} enterDelay={400} arrow>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {title}
                  <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.5, color: 'text.disabled' }} />
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                {title}
              </Typography>
            )}
          </Stack>
          {/* Value + change/helper stacked on same row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ gap: 1, flex: 1, position: 'relative', minHeight: 36 }}
          >
            {loading && (
              <Skeleton variant="text" width={48} sx={{ fontSize: '2rem', position: 'absolute', top: 0, left: 0 }} />
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                color: 'text.primary',
                visibility: loading ? 'hidden' : 'visible',
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {/* Change indicator + helper text stacked vertically —
                always render the container so height is stable */}
            <Stack alignItems="flex-end" spacing={0} sx={{ visibility: loading ? 'hidden' : 'visible' }}>
              {changeEl}
              {subStat && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: subStatColor || 'success.main',
                  }}
                >
                  {subStat}
                </Typography>
              )}
              {helperText && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.2, color: 'text.secondary' }}>
                  {helperText}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      </CardContent>
      {progress != null && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 4, '& .MuiLinearProgress-bar': { bgcolor: accentColor } }}
        />
      )}
    </Card>
  );
}
